import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';

interface Feature {
  title: string;
  description: string;
  image: string;
  ctaText?: string;
  ctaLink?: string;
}

interface FeatureBlocksProps {
  title?: string;
  subtitle?: string;
  features: Feature[];
  showMainCta?: boolean;
  mainCtaText?: string;
  mainCtaLink?: string;
}

const FeatureBlocksContainer = styled.section`
  padding: 5rem 1rem;
`;

const FeatureBlocksHeader = styled.div`
  text-align: center;
  max-width: 800px;
  margin: 0 auto 4rem;
`;

const FeatureBlocksTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  
  @media (min-width: 768px) {
    font-size: 3rem;
  }
`;

const FeatureBlocksSubtitle = styled.p`
  font-size: 1.2rem;
  color: var(--gray);
  margin-bottom: 2rem;
`;

const FeaturesList = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const FeatureBlock = styled.div<{ isEven: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 6rem;
  
  &:last-child {
    margin-bottom: 4rem;
  }
  
  @media (min-width: 992px) {
    flex-direction: ${props => props.isEven ? 'row-reverse' : 'row'};
    gap: 4rem;
  }
`;

const FeatureImageContainer = styled.div`
  flex: 1;
  margin-bottom: 2rem;
  
  @media (min-width: 992px) {
    margin-bottom: 0;
  }
`;

const FeatureImage = styled.img`
  width: 100%;
  height: auto;
  border-radius: 1rem;
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
`;

const FeatureContent = styled.div`
  flex: 1;
  text-align: center;
  
  @media (min-width: 992px) {
    text-align: left;
  }
`;

const FeatureTitle = styled.h3`
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  color: var(--gray-dark);
`;

const FeatureDescription = styled.p`
  font-size: 1.1rem;
  line-height: 1.7;
  color: var(--gray);
  margin-bottom: 2rem;
`;

const FeatureButton = styled(Link)`
  display: inline-block;
  background-color: var(--blue);
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 0.5rem;
  text-decoration: none;
  font-weight: 500;
  transition: background-color 0.3s ease, transform 0.3s ease;
  
  &:hover {
    background-color: var(--indigo);
    color: white;
    transform: translateY(-3px);
    text-decoration: none;
  }
`;

const MainCtaContainer = styled.div`
  text-align: center;
  max-width: 800px;
  margin: 0 auto;
`;

const MainCtaButton = styled(Link)`
  display: inline-block;
  background-color: var(--blue);
  color: white;
  font-size: 1.1rem;
  font-weight: 500;
  padding: 1rem 2rem;
  border-radius: 0.5rem;
  text-decoration: none;
  transition: background-color 0.3s ease, transform 0.3s ease;
  
  &:hover {
    background-color: var(--indigo);
    color: white;
    transform: translateY(-3px);
    text-decoration: none;
  }
`;

const FeatureBlocks: React.FC<FeatureBlocksProps> = ({
  title = "Crea tu web a tu manera",
  subtitle = "Personaliza fácilmente cada aspecto de tu página web con nuestras herramientas intuitivas",
  features,
  showMainCta = true,
  mainCtaText = "Comenzar ahora",
  mainCtaLink = "/reserva"
}) => {
  return (
    <FeatureBlocksContainer>
      <FeatureBlocksHeader>
        <FeatureBlocksTitle>{title}</FeatureBlocksTitle>
        {subtitle && <FeatureBlocksSubtitle>{subtitle}</FeatureBlocksSubtitle>}
      </FeatureBlocksHeader>
      
      <FeaturesList>
        {features.map((feature, index) => (
          <FeatureBlock key={index} isEven={index % 2 === 1}>
            <FeatureImageContainer>
              <FeatureImage src={feature.image} alt={feature.title} />
            </FeatureImageContainer>
            <FeatureContent>
              <FeatureTitle>{feature.title}</FeatureTitle>
              <FeatureDescription>{feature.description}</FeatureDescription>
              {feature.ctaText && feature.ctaLink && (
                <FeatureButton to={feature.ctaLink}>
                  {feature.ctaText}
                </FeatureButton>
              )}
            </FeatureContent>
          </FeatureBlock>
        ))}
      </FeaturesList>
      
      {showMainCta && (
        <MainCtaContainer>
          <MainCtaButton to={mainCtaLink}>
            {mainCtaText}
          </MainCtaButton>
        </MainCtaContainer>
      )}
    </FeatureBlocksContainer>
  );
};

export default FeatureBlocks; 